import React, { useState } from 'react';
import { Form as FinalForm } from 'react-final-form';
import { FormattedMessage } from '../../../util/reactIntl';
import { formatDateWithProximity } from '../../../util/dates';
import { composeValidators, required, validComment } from '../../../util/validators';
import { Form, FieldTextInput, SecondaryButton, Avatar, IconSpinner } from '../../../components';
import css from './SectionComments.module.css';

const SectionComments = (props) => {
    const {
        intl,
        comments = [],
        postCommentInProgress,
        postCommentError,
        onPostComment,
        listingId,
        listingTitle,
        listingAuthorId,
        userId,
        commentsUsers,
        config,
        isOwnListing,
        currentUserState,
        currentUserCommentsDisabled,
    } = props;

    const [retryAttempts, setRetryAttempts] = useState(0);
    const [lastComment, setLastComment] = useState(null);
    const [showRetryInfo, setShowRetryInfo] = useState(false);

    const commentsAreEmpty = comments.length == 0;
    const approvedComments = comments.filter(c => c.approved || c.userId == userId);

    const initialValues = {
        comment: "",
        dummy: new Date().getTime(),
    };

    // Auto-retry if there was an error and we have a saved comment to retry
    React.useEffect(() => {
        if (postCommentError && lastComment && retryAttempts < 2) {
            setShowRetryInfo(true);
            const timer = setTimeout(() => {
                setRetryAttempts(retryAttempts + 1);
                handleSubmitComment(lastComment);
            }, 3000);
            return () => clearTimeout(timer);
        }
    }, [postCommentError, lastComment]);

    const handleSubmitComment = (commentText) => {
        const newComment = {
            userId,
            approved: true,
            text: commentText,
            date: new Date().getTime(),
        };

        setLastComment(commentText);

        const newComments = [...comments, newComment];

        onPostComment({
            id: listingId,
            listingTitle,
            listingAuthorId,
            comments: newComments,
            config,
            isOwnListing,
        }).catch(() => {
            // Error is handled by the redux action
            // UI feedback is shown based on postCommentError state
        });
    };

    return <div className={css.root}>
        <div className={css.SectionCommentsContainer}>
            <h4 className={css.orderPanelTitle}>
                <FormattedMessage id="ListingPage.commentHeader1" />
            </h4>
            <h5 className={css.subTitle}>
                <FormattedMessage id="ListingPage.commentSubTitle1" />
            </h5>
            {postCommentError && (
                <div className={css.errorContainer}>
                    <p className={css.error}>
                        {postCommentError?.message || 'Comment could not be saved. Please try again.'}
                    </p>
                    {showRetryInfo && (
                        <p className={css.retryInfo}>
                            <IconSpinner /> Trying to resend your comment...
                        </p>
                    )}
                    {retryAttempts >= 2 && (
                        <SecondaryButton
                            onClick={() => {
                                setRetryAttempts(0);
                                handleSubmitComment(lastComment);
                            }}
                            className={css.retryButton}
                        >
                            Try again
                        </SecondaryButton>
                    )}
                </div>
            )}

            {approvedComments.length > 0 && <div className={css.commentsWrapper}>
                {approvedComments.map(comment => {
                    const { date, text, userId } = comment;
                    const avatarUser = commentsUsers.find(u => u.id.uuid == userId);
                    const userName = avatarUser?.attributes?.profile?.displayName;
                    const formattedDate = formatDateWithProximity(
                        new Date(date),
                        intl,
                        intl.formatMessage({ id: 'TransactionPage.ActivityFeed.today' }),
                    );

                    if (!avatarUser) {
                        return <div key={date} className={css.comment}>
                            <div className={css.commentContent}>
                                <div>
                                    <FormattedMessage id="SectionComments.commentDeletedUser" />
                                </div>
                                <div>
                                    <span className={css.date}>{formattedDate}</span>
                                </div>
                            </div>
                        </div>
                    }

                    return <div key={date} className={css.comment}>
                        <div>
                            <Avatar user={avatarUser} />
                        </div>
                        <div className={css.commentContent}>
                            <div>
                                <span className={css.userName}> {userName}{" "}</span>
                                <span className={css.text}>{text} </span>
                            </div>
                            <div>
                                <span className={css.date}>{formattedDate}</span>
                            </div>
                        </div>
                    </div>
                })}
            </div>}

            <div className={css.postComment}>
                <FinalForm
                    onSubmit={(v) => handleSubmitComment(v.comment)}
                    initialValues={initialValues}
                    render={fieldRenderProps => {
                        const {
                            disabled,
                            handleSubmit,
                            invalid,
                            sendReviewInProgress,
                            form,
                        } = fieldRenderProps;

                        const submitInProgress = sendReviewInProgress;
                        const currentUserIsActive = currentUserState == 'active' && !currentUserCommentsDisabled;
                        const submitDisabled = invalid || disabled || submitInProgress || postCommentInProgress || !currentUserIsActive;
                        const commentPlaceHolder = intl.formatMessage({ id: commentsAreEmpty ? 'ListingPage.SectionComments.commentPlaceholder.first1' : 'ListingPage.SectionComments.commentPlaceholder.after1' });

                        // Reset form after successful submission
                        React.useEffect(() => {
                            if (!postCommentInProgress && !postCommentError && lastComment) {
                                form.reset();
                                setLastComment(null);
                                setRetryAttempts(0);
                                setShowRetryInfo(false);
                            }
                        }, [postCommentInProgress, postCommentError, lastComment]);

                        return (
                            <Form onSubmit={handleSubmit}>
                                <div className={css.container}>
                                    {postCommentInProgress ? null : <FieldTextInput
                                        type="text"
                                        id="comment"
                                        name="comment"
                                        placeholder={commentPlaceHolder}
                                        validate={composeValidators(required("Required"), validComment(intl.formatMessage({ id: 'ListingPage.SectionComments.validCommentError' })))}
                                    />}
                                    <p className={css.information}><FormattedMessage id="ListingPage.information" /></p>
                                    {!currentUserIsActive && userId && <p> <FormattedMessage id="ListingPage.SectionComments.currentUserIsNotActive" /></p>}
                                    {!userId && <p> <FormattedMessage id="ListingPage.SectionComments.notLoggedIn" /></p>}
                                    <div className={css.buttonWrapper}>
                                        <SecondaryButton
                                            className={`${css.submitButton} ${submitDisabled ? css.submitButtonDisabled : ''}`}
                                            type="submit"
                                            disabled={submitDisabled}
                                            inProgress={submitInProgress || postCommentInProgress}
                                        >
                                            <FormattedMessage id="ListingPage.SectionComments.submit" />
                                        </SecondaryButton>
                                    </div>
                                    {
                                        !isOwnListing && <hr className={css.line} />
                                    }
                                </div>
                            </Form>
                        );
                    }}
                />
            </div>
        </div>
    </div>
};

export default SectionComments;